/**
 * Utility functions for determining super user status
 *
 * This module provides a centralized way to determine if the current user
 * is a super user, with proper order of operations and fallback values.
 *
 * @module superUserUtils
 */

import { storage } from '@uhg-abyss/web/tools/storage';

import { getLagoonConfigValue } from '../../../utils/providerSearch.utils';
import { loginNotRequired } from '../../../utils/session.utils';
import { Constants } from '../../Constants';

/**
 * Determines if the current user is a super user with the following priority:
 * 0. Explicit override from parent (if provided) - handles both boolean and string values
 * 1. Guest user check (returns false for guest users)
 * 2. Session storage (fastest, most reliable for active sessions)
 * 3. Lagoon config hostname check (fallback when session storage not set)
 * 4. Default to false if all sources fail
 *
 * @param allConfigs - Lagoon configuration accessor function (optional)
 * @param isSuperUserOverride - Explicit isSuperUser value from parent component (boolean or string, optional, highest priority)
 * @returns boolean - true if user is a super user, false otherwise
 *
 * @example
 * ```typescript
 * // With explicit boolean override
 * const isSuperUser = determineIsSuperUser(allConfigs, true);
 *
 * // With string override (handles HTML attributes)
 * const isSuperUser = determineIsSuperUser(allConfigs, "true");
 *
 * // With Lagoon config only
 * const allConfigs = useLagoon('config');
 * const isSuperUser = determineIsSuperUser(allConfigs);
 *
 * // Without any parameters (will use session storage + guest check only)
 * const isSuperUser = determineIsSuperUser();
 * ```
 */
export const determineIsSuperUser = (
  allConfigs?: any,
  isSuperUserOverride?: boolean | string
): boolean => {
  // Priority 0: Explicit override from parent component (highest priority)
  if (isSuperUserOverride !== undefined && isSuperUserOverride !== null) {
    // Handle string values (e.g., "true", "false") that might come from HTML attributes
    if (typeof isSuperUserOverride === 'string') {
      return isSuperUserOverride.toLowerCase() === 'true';
    }

    // Return boolean value directly
    return isSuperUserOverride;
  }

  // Priority 1: Guest users are never super users
  if (loginNotRequired()) {
    return false;
  }

  // Priority 2: Check session storage first (fastest check)
  const isSuperUserFromSession = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );

  if (isSuperUserFromSession !== null && isSuperUserFromSession !== undefined) {
    return isSuperUserFromSession;
  }

  // Priority 3: If no session value and configs are not available, return fallback
  if (!allConfigs) {
    return false;
  }

  // Priority 4: Check hostname against Lagoon config
  const hostname = window?.location?.hostname?.toLowerCase() ?? '';
  const includedHostNamesForSuperUser = getLagoonConfigValue(
    allConfigs,
    'INCLUDED_HOST_NAMES_FOR_SUPER_USER'
  );

  if (!includedHostNamesForSuperUser) {
    return false;
  }

  const hostNames = includedHostNamesForSuperUser
    .split(',')
    .filter((s: string) => s && s.trim() !== '');

  // Return true if current hostname matches any configured hostname (case-insensitive)
  return hostNames.some((host: string) =>
    hostname.includes(host.toLowerCase().trim())
  );
};

/**
 * Hook to get isSuperUser status with Lagoon config
 * This is a convenience wrapper around determineIsSuperUser for use in React components
 *
 * @param allConfigs - Lagoon configuration accessor function
 * @param isSuperUserOverride - Explicit isSuperUser value from parent component (boolean or string, optional, highest priority)
 * @returns boolean - true if user is a super user, false otherwise
 *
 * @example
 * ```typescript
 * const allConfigs = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
 * const isSuperUser = getIsSuperUser(allConfigs);
 *
 * // With override from parent (boolean or string)
 * const isSuperUser = getIsSuperUser(allConfigs, true);
 * const isSuperUser = getIsSuperUser(allConfigs, "true");
 * ```
 */
export const getIsSuperUser = (
  allConfigs?: any,
  isSuperUserOverride?: boolean | string
): boolean => determineIsSuperUser(allConfigs, isSuperUserOverride);

/**
 * Simple check for super user status using only session storage
 * Use this when Lagoon config is not available or not needed
 *
 * @returns boolean - true if user is a super user based on session storage, false otherwise
 *
 * @example
 * ```typescript
 * const isSuperUser = getIsSuperUserFromSession();
 * ```
 */
export const getIsSuperUserFromSession = (): boolean => {
  if (loginNotRequired()) {
    return false;
  }

  return (
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false
  );
};
